import { useMcpTool } from 'webmcp-react'
import { uiStore } from 'shared/store/uiStore'
import { InboxesApi } from 'entities/Inbox'
import { BroadcastsRoutes } from 'entities/ExportRouters'
import {
  listBroadcastNumbersSchema,
  openBroadcastCreateSchema,
} from 'entities/WebMcp/model/schemas'
import { okResult, errorResult } from 'entities/WebMcp/lib/toolResult'
import { waitForToolRegistered } from 'entities/WebMcp/lib/waitForTool'

/**
 * Registers the global broadcast tools. Renders nothing.
 * - list_broadcast_numbers (read): eligible sending numbers, from the same source
 *   and filter as the broadcast form's "Send from" picker.
 * - open_broadcast_create (navigation): opens the broadcast create form, which
 *   then registers the set_broadcast_* fill tools.
 *
 * The form itself is filled through page-scoped fill tools (set_broadcast_*)
 * registered by BroadcastFillTools when the create/edit form is open. See the
 * WebMCP contextual-fill-tools design.
 */
// How many sending numbers to offer in a choice card. An organization can have
// dozens; an option per number makes a tool call large enough to look like a
// hang, and a card that long is not a choice anyone wants to read.
const OFFER_LIMIT = 8

/**
 * The whole broadcast procedure, returned when the form opens rather than kept in
 * the system prompt. It arrives at the one moment it applies, and it reaches any
 * agent calling these tools, not only ours (build rules, Articles I and II).
 */
const BROADCAST_NEXT_STEPS =
  'The form is open. Re-check your available tools, then: (1) Ask for any fields the user has not already given, in ONE message, as a short numbered list so each field is distinct, using this order and wording: Name — what do you want to call this broadcast?; Message — what is the message you want to send?; Audience — who should receive it (a tag, specific contacts, or both)?; Sending inbox — which phone number should it go out from? Ask only for the ones still missing, and never one at a time when several are missing. (2) Resolve the audience to ids with list_tags, and the sending number to a number_id with list_broadcast_numbers plus ask_user_choice. (3) Only once every field is settled, call set_broadcast_details, set_broadcast_audience, and set_broadcast_sender in ONE reply: filling needs the user to approve it, and calling them together makes the whole broadcast a single approval card instead of one per field. (4) The user reviews and sends from the form itself, so never assume it was sent; mention that the Test button next to Review/Send lets them send it to a number of their choosing first.'

export const BroadcastTools = () => {
  useMcpTool({
    name: 'open_broadcast_create',
    description:
      'Open the broadcast create form (Outbound). Call this FIRST for any request to create or send a broadcast, from any screen, before gathering any field: opening the form is what registers set_broadcast_details, set_broadcast_audience, and set_broadcast_sender, so asking the user for broadcast details before calling this is wrong even when those tools are not listed yet. Re-check the available tools after opening it.',
    input: openBroadcastCreateSchema,
    annotations: { readOnlyHint: true },
    handler: async () => {
      try {
        uiStore.changeRouteViaVue({
          path: `/${BroadcastsRoutes.root}/${BroadcastsRoutes.create}`,
        })
        // Resolve only once the form has mounted and registered its fill tools, so
        // the agent's next turn sees them and does not call them before they exist.
        const ready = await waitForToolRegistered('set_broadcast_details', 6000)
        return okResult(
          {
            opened: true,
            form_ready: ready,
          },
          ready
            ? BROADCAST_NEXT_STEPS
            : 'The form is opening but its fill tools are not registered yet. Re-check available tools; if the set_broadcast_* tools are still missing, call open_broadcast_create again before trying to fill.'
        )
      } catch (e) {
        return errorResult(e)
      }
    },
  })

  useMcpTool({
    name: 'list_broadcast_numbers',
    description:
      'List sending numbers eligible for a broadcast (verified / broadcast-ready), the same options the broadcast form offers. Present the results with ask_user_choice so the user can select one — never write them out as a list in your reply. Use the chosen number_id with set_broadcast_sender on the open broadcast form.',
    input: listBroadcastNumbersSchema,
    annotations: { readOnlyHint: true },
    handler: async () => {
      try {
        const { data } = await InboxesApi.getTeamsSearch({
          page: 1,
          per_page: 50,
          exclude_deprioritized: 1,
          by_all_members: true,
        })
        // Mirror the form's SMS filter: a toll-free number is only eligible when verified.
        const results = data.results.flatMap((inbox) =>
          (inbox.numbers ?? [])
            .filter((n) => !n.is_toll_free || n.verified_status === 'Verified')
            .map((n) => ({
              number_id: n.id,
              number: n.formatted_number || n.number,
              inbox: inbox.name,
              type: n.type,
              verified: n.verified_status === 'Verified',
            }))
        )
        // Verified numbers first: they are the ones a broadcast can actually
        // go out from, so they belong at the top of a capped shortlist.
        const ranked = [...results].sort((a, b) => Number(b.verified) - Number(a.verified))
        // The presentation rule travels with the data: a static description is
        // easy to lose behind a long list. The cap matters as much as the rule
        // — an option per number means a tool call with dozens of options,
        // which is slow enough to read as the agent having hung.
        return okResult(
          {
            total: ranked.length,
            results: ranked,
          },
          `Do not list these numbers in your reply. Call ask_user_choice so the user can select one, with at most ${OFFER_LIMIT} options (value = number_id, label = the number and its inbox), the ones listed first here, plus a final "I will choose on the screen" option. Never build an option for every number. If the user names a number instead, match it here and use its number_id directly.`
        )
      } catch (e) {
        return errorResult(e)
      }
    },
  })

  return null
}
